import { AxiosInstance, AxiosRequestConfig } from 'axios';
import isEmpty from 'lodash/isEmpty';

import { AuditAPIApiFactory, AuditV2APIApiFactory } from '@cast/openapi';
import {
  AuditLogEntry,
  ScrollableList,
  AuditLogFilter,
  AuditLogListEntry,
} from '@cast/types';

import { AXIOS_CONFIGURATION } from '../constants';

export const AuditLogApiFactory = (client: AxiosInstance) => {
  const generatedApi = AuditAPIApiFactory(AXIOS_CONFIGURATION, '', client);
  const generatedV2Api = AuditV2APIApiFactory(AXIOS_CONFIGURATION, '', client);

  return {
    getAuditListEventDetail: (id: string, time: string) => {
      return generatedApi.auditAPIGetAuditEvent({
        id,
        time,
      });
    },
    getAuditListEvent: (filter?: AuditLogFilter) => {
      let labelString = '';
      const { labels, ...restFilter } = filter || {};

      if (filter && !isEmpty(labels)) {
        labelString += Object.entries(labels)
          .map(([key, value]) => `labels[${key}]=${value}`)
          .join('&');
      }

      return client.get<ScrollableList<AuditLogListEntry>>(
        `/v1/audit-events${labelString ? `?${labelString}` : ''}`,
        {
          params: { 'page.limit': 20, ...restFilter },
        }
      );
    },
    getAuditLog: (
      filter?: AuditLogFilter,
      options: AxiosRequestConfig = {}
    ) => {
      let labelString = '';
      const { labels, ...restFilter } = filter || {};

      // https://spec.openapis.org/oas/v3.1.0.html#style-values deepObject style
      if (filter && !isEmpty(labels)) {
        labelString += Object.entries(labels)
          .map(([key, value]) => `labels[${key}]=${value}`)
          .join('&');
      }

      return client.get<ScrollableList<AuditLogEntry>>(
        `/v1/audit${labelString ? `?${labelString}` : ''}`,
        {
          params: { 'page.limit': 20, ...restFilter },
          ...options,
        }
      );
    },
    getAuditV2Event: (id: string, recordedAt: string) => {
      return generatedV2Api.auditV2APIGetAuditEvent({ id, recordedAt });
    },
    getAuditV2RelatedEvents: (
      id: string,
      params?: { pageLimit?: string; pageCursor?: string; recordedAt?: string }
    ) => {
      return generatedV2Api.auditV2APIGetRelatedAuditEvents({
        id,
        ...params,
      });
    },
    listAuditV2Events: (params?: {
      pageLimit?: string;
      pageCursor?: string;
      fromDate?: string;
      toDate?: string;
      filterSearch?: string;
      filterClusters?: Array<string>;
      filterDomains?: Array<string>;
      filterResources?: Array<string>;
      filterActions?: Array<string>;
      filterSources?: Array<string>;
      filterSeverity?: Array<string>;
      // sort.field / sort.order are not yet in the generated client — pass via options.params
      sortField?: string;
      sortOrder?: string;
    }) => {
      const { sortField, sortOrder, ...rest } = params ?? {};
      return generatedV2Api.auditV2APIListAuditEvents(rest, {
        params: {
          ...(sortField && { 'sort.field': sortField }),
          ...(sortOrder && { 'sort.order': sortOrder }),
        },
      });
    },
    getAuditHistogram: (params?: {
      fromDate?: string;
      toDate?: string;
      filterSearch?: string;
      filterClusters?: Array<string>;
      filterDomains?: Array<string>;
      filterResources?: Array<string>;
      filterActions?: Array<string>;
      filterSources?: Array<string>;
      filterSeverity?: Array<string>;
    }) => {
      return generatedV2Api.auditV2APIGetAuditHistogram(params ?? {});
    },
    getAuditStats: (params?: {
      fromDate?: string;
      toDate?: string;
      filterSearch?: string;
      filterClusters?: Array<string>;
      filterDomains?: Array<string>;
      filterResources?: Array<string>;
      filterActions?: Array<string>;
      filterSources?: Array<string>;
      filterSeverity?: Array<string>;
    }) => {
      return generatedV2Api.auditV2APIGetAuditStats(params ?? {});
    },
  };
};
